/**
 * Authenticated CRM session beacon: emit heartbeats while the tab is visible
 * and focused. Route changes flush immediately. No keystroke/content logging.
 *
 * Includes lastInputAgeSec + idleState (active / reading / idle) so the same
 * stream supports both strict and presence measures.
 */
import { useEffect, useRef } from 'react';
import type { CrmActivityContext } from '../utils/crmActivityContext';
import { getCrmActivitySessionId } from '../utils/crmActivitySessionId';
import { saveCrmSessionHeartbeat } from '../utils/crmSessionHeartbeatService';
import {
  IDLE_THRESHOLD_MS,
  PRESENCE_IDLE_THRESHOLD_MS,
  isWindowFocused,
} from './useIdleAwareTimeSegments';
import type { CrmHeartbeatIdleState } from '../types';

export const CRM_SESSION_BEACON_INTERVAL_MS = 60 * 1000;

interface UseCrmSessionBeaconOptions {
  userId?: string;
  userEmail?: string;
  userName?: string;
  enabled?: boolean;
  context: CrmActivityContext;
}

function resolveIdleState(lastInputAt: number, now: number): CrmHeartbeatIdleState {
  const age = now - lastInputAt;
  if (age <= IDLE_THRESHOLD_MS) return 'active';
  if (age <= PRESENCE_IDLE_THRESHOLD_MS) return 'reading';
  return 'idle';
}

export function useCrmSessionBeacon({
  userId,
  userEmail,
  userName,
  enabled = true,
  context,
}: UseCrmSessionBeaconOptions) {
  const contextRef = useRef(context);
  const userIdRef = useRef(userId);
  const userEmailRef = useRef(userEmail);
  const userNameRef = useRef(userName);
  const lastInputRef = useRef(Date.now());
  contextRef.current = context;
  userIdRef.current = userId;
  userEmailRef.current = userEmail;
  userNameRef.current = userName;

  const emitRef = useRef<() => Promise<void>>(async () => undefined);
  emitRef.current = async () => {
    const uid = userIdRef.current;
    if (!uid) return;
    const visible = typeof document !== 'undefined' && !document.hidden;
    const focused = isWindowFocused();
    // Still record focused/visible separately when emitting; skip write if neither
    // useful for presence (matches prior behavior: only visible+focused pings).
    if (!visible || !focused) return;

    const now = Date.now();
    const lastInputAgeSec = Math.max(0, Math.round((now - lastInputRef.current) / 1000));
    const idleState = resolveIdleState(lastInputRef.current, now);
    const ctx = contextRef.current;
    try {
      await saveCrmSessionHeartbeat({
        userId: uid,
        userEmail: userEmailRef.current || '',
        userName: userNameRef.current || 'Unknown',
        timestamp: new Date(now),
        path: ctx.path,
        pageKey: ctx.pageKey,
        pageLabel: ctx.pageLabel,
        activityClass: ctx.activityClass,
        jobId: ctx.jobId,
        dealId: ctx.dealId,
        entityType: ctx.entityType,
        entityId: ctx.entityId,
        entityName: ctx.entityName,
        moduleKey: ctx.moduleKey,
        moduleLabel: ctx.moduleLabel,
        viewKey: ctx.viewKey || ctx.moduleKey,
        viewLabel: ctx.viewLabel || ctx.moduleLabel,
        sessionId: getCrmActivitySessionId(),
        lastInputAgeSec,
        idleState,
        focused: true,
        visible: true,
        createdAt: new Date(now),
      });
    } catch (err) {
      console.error('[CrmSessionBeacon] Failed to save heartbeat:', err);
    }
  };

  useEffect(() => {
    if (!enabled || !userId) return;
    void emitRef.current();
    const timer = setInterval(() => {
      void emitRef.current();
    }, CRM_SESSION_BEACON_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, userId]);

  useEffect(() => {
    if (!enabled || !userId) return;
    void emitRef.current();
  }, [
    enabled,
    userId,
    context.path,
    context.pageKey,
    context.moduleKey,
    context.viewKey,
    context.jobId,
    context.dealId,
    context.entityType,
    context.entityId,
    context.activityClass,
  ]);

  useEffect(() => {
    if (!enabled || !userId) return;
    const bump = () => {
      lastInputRef.current = Date.now();
    };
    const opts: AddEventListenerOptions = { capture: true, passive: true };
    const events: (keyof WindowEventMap)[] = [
      'pointerdown',
      'keydown',
      'touchstart',
      'wheel',
      'scroll',
      'mousemove',
      'pointermove',
      'keyup',
      'focus',
    ];
    for (const ev of events) window.addEventListener(ev, bump, opts);
    const onVis = () => {
      if (!document.hidden && isWindowFocused()) {
        lastInputRef.current = Date.now();
        void emitRef.current();
      }
    };
    const onFocus = () => {
      if (!document.hidden) {
        lastInputRef.current = Date.now();
        void emitRef.current();
      }
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    return () => {
      for (const ev of events) window.removeEventListener(ev, bump, opts);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
    };
  }, [enabled, userId]);
}
